import React, { Fragment, lazy, Suspense, useCallback, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  styled,
  Chip,
  Skeleton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  IconButton,
  DialogActions,
  ListItemIcon,
} from "@mui/material";
import { Link } from "react-router-dom";
import { CartContext } from "../../contexts/CartContext";
import PropTypes from "prop-types";
import LazyLoad from "react-lazyload";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import YouTubeIcon from "@mui/icons-material/YouTube";
import CloseIcon from "@mui/icons-material/Close";
import LaunchIcon from "@mui/icons-material/Launch";
import { useTranslation } from "react-i18next";

const StyledCard = styled(Card)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  border: "none",
  borderRadius: theme.spacing(1),
  transition: "all 0.3s ease",
  backgroundColor: "#fff",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
    "& .book-title": {
      color: theme.palette.primary.main,
    },
  },
}));

const BookCover = styled("img")({
  width: "100%",
  aspectRatio: "3/4",
  objectFit: "cover",
  transition: "transform 0.3s ease, opacity 0.3s ease",
  opacity: 1,
  "&.loading": {
    opacity: 0,
  },
});

const ImagePlaceholder = styled(Skeleton)(({ theme }) => ({
  width: "100%",
  height: "100%",
  position: "absolute",
  top: 0,
  left: 0,
  transform: "none",
  transformOrigin: "0 0",
}));

const CardContentStyled = styled(CardContent)({
  padding: "12px",
  flex: 1,
  display: "flex",
  flexDirection: "column",
});

const BookTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.875rem",
  lineHeight: 1.4,
  marginBottom: "4px",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
  height: "2.5em",
  color: theme.palette.text.primary,
  transition: "color 0.2s ease",
  textDecoration: "none",
}));

const PublishInfo = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.75rem",
  marginBottom: "8px",
}));

const PriceContainer = styled(Box)({
  marginTop: "auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
});

const PriceWrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
});

const CurrentPrice = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  fontWeight: 600,
  color: theme.palette.primary.main,
  lineHeight: 1.2,
}));

const OriginalPrice = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  textDecoration: "line-through",
  color: theme.palette.text.secondary,
  lineHeight: 1,
}));

const AddToCartButton = styled(Button)(({ theme }) => ({
  minWidth: "auto",
  padding: "4px 12px",
  fontSize: "0.75rem",
  textTransform: "none",
  fontWeight: 500,
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(0.5),
  "&:hover": {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    borderColor: theme.palette.primary.main,
  },
  "&.added": {
    backgroundColor: theme.palette.success.main,
    color: "#fff",
    borderColor: theme.palette.success.main,
  },
}));

const BookTypeChip = styled(Chip)(({ theme }) => ({
  position: "absolute",
  top: "8px",
  left: "8px",
  backgroundColor: "rgba(255, 255, 255, 0.9)",
  fontSize: "0.675rem",
  height: "20px",
  fontWeight: 500,
  borderRadius: "3px",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
}));

const DiscountChip = styled(Chip)(({ theme }) => ({
  position: "absolute",
  top: "8px",
  right: "8px",
  backgroundColor: theme.palette.error.main,
  color: "#fff",
  fontSize: "0.675rem",
  height: "20px",
  fontWeight: 600,
  borderRadius: "3px",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
}));

const BookCoverPlaceholder = styled(Box)(({ theme }) => ({
  width: "100%",
  aspectRatio: "3/4",
  backgroundColor: theme.palette.grey[100],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.palette.text.secondary,
  fontSize: "0.875rem",
}));

const LazyBookCover = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  aspectRatio: "3/4",
  backgroundColor: theme.palette.grey[50],
  overflow: "hidden",
}));

const PDFReader = lazy(() => import("../../components/readers/PDFReader"));
const AudioPlayer = lazy(() => import("../../components/readers/AudioPlayer"));

const BookCoverImage = React.memo(({ src, alt, to, isAudioBook }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  const detailsPath = `/books/${to}`;

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setError(true);
  };

  return (
    <Link to={detailsPath} style={{ position: "relative", display: "block" }}>
      <LazyLoad
        height={0}
        offset={100}
        resize={true}
        scroll={true}
        placeholder={
          <BookCoverPlaceholder>
            <Box sx={{ textAlign: "center", p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {t("audioBooks.card.loading")}
              </Typography>
            </Box>
          </BookCoverPlaceholder>
        }
      >
        <LazyBookCover>
          {isLoading && (
            <ImagePlaceholder variant="rectangular" animation="wave" />
          )}
          <BookCover
            src={src}
            alt={alt}
            loading="lazy"
            onLoad={handleLoad}
            onError={handleError}
            className={isLoading ? "loading" : ""}
            sx={{
              display: error ? "none" : "block",
              opacity: isLoading ? 0 : 1,
              transition: "opacity 0.3s ease-in-out",
            }}
          />
          {error && (
            <BookCoverPlaceholder>
              <Box sx={{ textAlign: "center", p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {t("audioBooks.card.imageNotAvailable")}
                </Typography>
              </Box>
            </BookCoverPlaceholder>
          )}
        </LazyBookCover>
      </LazyLoad>
    </Link>
  );
});

BookCoverImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  to: PropTypes.string.isRequired,
  isAudioBook: PropTypes.bool,
};

const BookTypeLabel = React.memo(({ format }) => (
  <BookTypeChip label={format} size="small" />
));

const DiscountLabel = React.memo(({ discount }) =>
  discount > 0 ? <DiscountChip label={`-${discount}%`} size="small" /> : null,
);

const BookInfo = React.memo(
  ({ title, publicationDate, pages, bookId, isAudioBook }) => {
    const { t } = useTranslation();
    const detailsPath = isAudioBook
      ? `/audio-books/${bookId}`
      : `/books/${bookId}`;

    return (
      <>
        <BookTitle component={Link} to={detailsPath} className="book-title">
          {title}
        </BookTitle>
        <PublishInfo>
          {formatDate(publicationDate)} • {pages} {t("audioBooks.card.pages")}
        </PublishInfo>
      </>
    );
  },
);

const PriceInfo = React.memo(({ price, originalPrice }) => (
  <PriceWrapper>
    <CurrentPrice>${formatPrice(price)}</CurrentPrice>
    {originalPrice && (
      <OriginalPrice>${formatPrice(originalPrice)}</OriginalPrice>
    )}
  </PriceWrapper>
));

// Memoize utility functions
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });
};

const formatPrice = (price) => {
  return parseFloat(price).toFixed(2);
};

const calculateDiscount = (originalPrice, price) => {
  if (!originalPrice) return 0;
  return Math.round(
    ((parseFloat(originalPrice) - parseFloat(price)) /
      parseFloat(originalPrice)) *
      100,
  );
};

const BookCardSkeleton = React.memo(() => (
  <StyledCard>
    <Skeleton
      variant="rectangular"
      sx={{
        width: "100%",
        aspectRatio: "3/4",
      }}
    />
    <CardContentStyled>
      <Skeleton width="80%" height={24} sx={{ mb: 1 }} />
      <Skeleton width="60%" height={20} sx={{ mb: 2 }} />
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Skeleton width={80} height={24} />
        <Skeleton width={60} height={32} />
      </Box>
    </CardContentStyled>
  </StyledCard>
));

const YouTubeDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: '100%',
    maxWidth: 500,
    borderRadius: theme.spacing(1),
  },
}));

const YouTubeListItem = styled(ListItem)(({ theme }) => ({
  margin: theme.spacing(1, 0),
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const PlayButton = styled(Button)(({ theme }) => ({
  minWidth: "auto",
  padding: "4px 12px",
  fontSize: "0.75rem",
  textTransform: "none",
  fontWeight: 500,
  backgroundColor: theme.palette.primary.main,
  color: "#fff",
  border: `1px solid ${theme.palette.primary.main}`,
  borderRadius: theme.spacing(0.5),
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const BookCard = React.memo(({ book, loading }) => {
  const { t } = useTranslation();
  const [readerOpen, setReaderOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(book?.collections?.[0] || null);
  const [isLoadingReader, setIsLoadingReader] = useState(false);
  const [youtubeLinksOpen, setYoutubeLinksOpen] = useState(false);

  const { addToCart, isInCart } = React.useContext(CartContext);
  const [isAdding, setIsAdding] = useState(false);

  // Move hooks before the conditional return
  const discount = useMemo(
    () => (book ? calculateDiscount(book.original_price, book.price) : 0),
    [book],
  );

  // Check if the book is an audio book with YouTube links
  const hasYoutubeLinks = useMemo(() => {
    if (!book || book.format !== "Audiobook") return false;
    return book.collections?.some(collection => !!collection.youtube_link);
  }, [book]);

  const handleReadOrPlay = () => {
    if (book.format === "Audiobook") {
      if (hasYoutubeLinks) {
        setYoutubeLinksOpen(true);
      } else {
        setReaderOpen(true);
        setIsLoadingReader(true);
      }
    } else {
      setReaderOpen(true);
      setIsLoadingReader(true);
    }
  };

  const handleYoutubeClick = (url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleAddToCart = useCallback(() => {
    if (!isInCart(book?.id)) {
      setIsAdding(true);
      addToCart({
        id: book.id,
        title: book.title,
        price: parseFloat(book.price),
        image: book.cover_image,
        format: book.format,
      });
      setTimeout(() => setIsAdding(false), 300);
    }
  }, [book, addToCart, isInCart]);

  if (loading) {
    return <BookCardSkeleton />;
  }

  if (!book) return null;

  const isAudioBook = book.format === "Audiobook";

  return (
    <Fragment>
      <StyledCard>
        <Box sx={{ position: "relative" }}>
          <BookCoverImage
            src={book.cover_image}
            alt={book.title}
            to={book.id}
            isAudioBook={isAudioBook}
          />
          <BookTypeLabel format={hasYoutubeLinks ? "YouTube" : book.format} />
          {discount > 0 && <DiscountLabel discount={discount} />}
        </Box>

        <CardContentStyled>
          <BookInfo
            title={book.title}
            publicationDate={book.publication_date}
            pages={book.pages}
            bookId={book.id}
            isAudioBook={isAudioBook}
          />

          <PriceContainer>
            <PriceInfo price={book.price} originalPrice={book.original_price} />

            {book.is_purchased ? (
              isAudioBook ? (
                <PlayButton 
                  onClick={handleReadOrPlay}
                  startIcon={<PlayArrowIcon />}
                >
                  {isLoadingReader ? <CircularProgress size={16} color="inherit" /> : t("audioBooks.card.play")}
                </PlayButton>
              ) : (
                <AddToCartButton onClick={handleReadOrPlay}>
                  {isLoadingReader ? <CircularProgress size={16} color="inherit" /> : t("audioBooks.card.read")}
                </AddToCartButton>
              )
            ) : (
              <AddToCartButton
                onClick={handleAddToCart}
                disabled={isInCart(book.id)}
                className={isInCart(book.id) ? "added" : ""}
              >
                {isInCart(book.id) ? t("audioBooks.card.added") : t("audioBooks.card.add")}
              </AddToCartButton>
            )}
          </PriceContainer>
        </CardContentStyled>
      </StyledCard>

      {/* YouTube Links Dialog */}
      <YouTubeDialog
        open={youtubeLinksOpen}
        onClose={() => setYoutubeLinksOpen(false)}
        aria-labelledby="youtube-dialog-title"
      >
        <DialogTitle id="youtube-dialog-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="div">
            {t("audioBooks.card.youtubeVideos")} {book.title}
          </Typography>
          <IconButton aria-label="close" onClick={() => setYoutubeLinksOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <List>
            {book.collections?.filter(c => c.youtube_link).map((collection, index) => (
              <YouTubeListItem 
                button 
                key={collection.id || index} 
                onClick={() => handleYoutubeClick(collection.youtube_link)}
              >
                <ListItemIcon>
                  <YouTubeIcon color="error" />
                </ListItemIcon>
                <ListItemText 
                  primary={collection.filename || `Video ${index + 1}`} 
                  secondary={collection.youtube_link}
                  primaryTypographyProps={{ fontWeight: 500 }}
                  secondaryTypographyProps={{ 
                    noWrap: true,
                    sx: { maxWidth: '220px' }
                  }}
                />
                <LaunchIcon fontSize="small" color="action" />
              </YouTubeListItem>
            ))}
            {(!book.collections || book.collections.filter(c => c.youtube_link).length === 0) && (
              <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
                {t("audioBooks.card.noVideos")}
              </Typography>
            )}
          </List>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', p: 2 }}>
          <Button 
            onClick={() => setYoutubeLinksOpen(false)} 
            variant="outlined"
          >
            {t("audioBooks.card.close")}
          </Button>
        </DialogActions>
      </YouTubeDialog>

      <Suspense fallback={<></>}>
        <Dialog
          fullScreen
          open={readerOpen}
          onClose={() => {
            setReaderOpen(false);
            setIsLoadingReader(false);
          }}
        >
          {selectedCollection && book.format === "PDF" ? (
            <PDFReader
              url={`${process.env.REACT_APP_API_URL}/books/${book.id}/read/?collection_id=${selectedCollection.id}`}
              onClose={() => {
                setReaderOpen(false);
                setIsLoadingReader(false);
              }}
              onLoad={() => setIsLoadingReader(false)}
            />
          ) : selectedCollection && book.format === "Audiobook" ? (
            <AudioPlayer
              url={`${process.env.REACT_APP_API_URL}/books/${book.id}/read/?collection_id=${selectedCollection.id}`}
              title={book.title}
              author={book.added_by_display?.join(", ")}
              onClose={() => {
                setReaderOpen(false);
                setIsLoadingReader(false);
              }}
              onLoad={() => setIsLoadingReader(false)}
            />
          ) : null}
        </Dialog>
      </Suspense>
    </Fragment>
  );
});

// Add display name for better debugging
BookCard.displayName = "BookCard";

BookCard.propTypes = {
  book: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    publication_date: PropTypes.string.isRequired,
    pages: PropTypes.number.isRequired,
    price: PropTypes.string.isRequired,
    original_price: PropTypes.string,
    cover_image: PropTypes.string.isRequired,
    format: PropTypes.string.isRequired,
    is_purchased: PropTypes.bool.isRequired,
    collections: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        filename: PropTypes.string,
        youtube_link: PropTypes.string
      })
    )
  }),
  loading: PropTypes.bool,
  isAudioBook: PropTypes.bool,
};

export default BookCard;
